import type { components } from "@/types/api";

export type AppCapability = components["schemas"]["AppCapability"];

interface CollaboratorLike {
  // The union plus the value pre-RBAC servers still send on the wire
  permission: components["schemas"]["CollaboratorProperties"]["permission"] | "Collaborator";
  isCurrentAccount?: boolean;
}

const OWNER_ONLY_LEGACY: AppCapability[] = [
  "app.manage",
  "app.delete",
  "app.transfer",
  "collaborators.manage",
];

export interface AppAccess {
  /** True when the server sent a capabilities array (post-RBAC server). */
  serverCapabilities: boolean;
  can: (capability: AppCapability) => boolean;
}

/**
 * Builds the capability checker for one app.
 *
 * When the server response carries `capabilities`, it is authoritative.
 * Against a pre-RBAC server the array is absent, so this falls back to the
 * legacy client-side inference: owner-only sections need the Owner row, and
 * everything else renders exactly as it did before roles existed. The server
 * still rejects anything the account cannot actually do.
 */
export function accessFrom(
  capabilities: readonly string[] | undefined,
  collaborators: Record<string, CollaboratorLike> | undefined,
): AppAccess {
  if (capabilities) {
    const set = new Set(capabilities);
    return { serverCapabilities: true, can: (capability) => set.has(capability) };
  }

  const isOwner = Object.values(collaborators ?? {}).some(
    (props) => props.isCurrentAccount && props.permission === "Owner",
  );

  return {
    serverCapabilities: false,
    can: (capability) => (OWNER_ONLY_LEGACY.includes(capability) ? isOwner : true),
  };
}
